/**
 * API surface — the small set of endpoints this app calls.
 *
 * Two intent-creation paths exist (balance vs precollect). The data-layer
 * handoff §3.5 recommends keeping them as two functions because their
 * validation rules are different enough that one shared function gets
 * messy. We follow that recommendation.
 *
 * `confirmPayment` is intentionally NOT here — payment confirmation is a
 * Stripe.js call (talks directly to Stripe's API), not a backend call. See
 * `stripe/usePaymentSubmit.ts` for the orchestration.
 */
import { request } from './client';
import type {
  AuthenticateRequest,
  AuthenticateResponse,
  CreateBalanceIntentRequest,
  CreatePaymentIntentResponse,
  CreatePrecollectIntentRequest,
  FetchCheckinBalanceResponse,
  FetchPaymentMeResponse,
  MagicLinkRequest,
  MagicLinkResponse,
} from './types';

/** POST /api/v1/auth/login — PIN + DOB authentication. */
export function authenticate(
  body: AuthenticateRequest,
): Promise<AuthenticateResponse> {
  return request<AuthenticateResponse>('POST', '/api/v1/auth/login', body, {
    unauthenticated: true,
  });
}

/**
 * POST /api/v1/auth/magic-link — magic-link + DOB authentication.
 *
 * New in v0.8.0. See `MagicLinkRequest` / `MagicLinkResponse` in
 * `./types.ts` for the contract.
 *
 * Per ledger #41: this is the only place the token is "consumed."
 * The MagicLinkLandingPage must NOT call this on GET / on mount —
 * only after the patient enters their DOB and submits.
 */
export function verifyMagicLink(
  body: MagicLinkRequest,
): Promise<MagicLinkResponse> {
  return request<MagicLinkResponse>('POST', '/api/v1/auth/magic-link', body, {
    unauthenticated: true,
  });
}

/**
 * POST /api/v1/auth/logout — best-effort. Backend invalidates the session
 * row so even an unexpired JWT stops working. Caller still clears storage
 * regardless of whether this resolves.
 */
export function logout(): Promise<void> {
  return request<void>('POST', '/api/v1/auth/logout', undefined, {
    handle401: 'ignore',
  });
}

/** GET /api/v1/payment/me — full payment context for the logged-in patient. */
export function fetchPaymentMe(): Promise<FetchPaymentMeResponse> {
  return request<FetchPaymentMeResponse>('GET', '/api/v1/payment/me');
}

/**
 * GET /api/v1/checkin/balance/:appointment_id — precollect context.
 *
 * Returns the upcoming appointment's estimate plus any past balance.
 */
export function fetchCheckinBalance(
  appointmentId: number,
): Promise<FetchCheckinBalanceResponse> {
  return request<FetchCheckinBalanceResponse>(
    'GET',
    `/api/v1/checkin/balance/${appointmentId}`,
  );
}

/** POST /api/v1/payment/charge — create Stripe intent for the balance flow. */
export function createBalanceIntent(
  body: CreateBalanceIntentRequest,
): Promise<CreatePaymentIntentResponse> {
  return request<CreatePaymentIntentResponse>(
    'POST',
    '/api/v1/payment/charge',
    body,
  );
}

/** POST /api/v1/checkin/payment — create Stripe intent for the precollect flow. */
export function createPrecollectIntent(
  body: CreatePrecollectIntentRequest,
): Promise<CreatePaymentIntentResponse> {
  return request<CreatePaymentIntentResponse>(
    'POST',
    '/api/v1/checkin/payment',
    body,
  );
}

// Re-export for convenience.
export { ApiError } from './client';
export * from './types';
